'use strict';

import React, {useEffect} from 'react';
import remark from 'remark';
import html from 'remark-html';

import {
  GET_CATEGORY_PAGES_COMPLETE,
  GET_SINGLE_CATEGORY_PAGE,
} from '/lib/api/graphql/query';
import Partners from '../components/partners';
import FullWidthComponent from '../components/categoryComponents/fullWidth';
import ProductsComponent
  from '../components/categoryComponents/productsComponent';
import CategoryOverview
  from '../components/categoryComponents/categoryOverview';
import MacroProducts from '../components/categoryComponents/macroProducts';
import client from '../apollo-client';
import {serverSideTranslations} from 'next-i18next/serverSideTranslations';
import {useTranslation} from 'next-i18next';
import _ from 'lodash';
import MetaHead from '../components/meta/MetaHead';
import {useRouter} from 'next/router';
import {
  generateBreadcrumbJsonLd,
  generateVideoJsonLd,
} from '../lib/utils/googleUtils';
import {getStrapiLocale} from '../lib/utils/locationUtils';
import {generateAlternativeUrlsFromLocalizations} from '../lib/utils/intlUtils';

export default function ModelPage({data, markdown, model: slug}) {

  const {t: translateSiteMeta} = useTranslation('site-meta');
  const [mainTitle, model] = slug.split('-');

  useEffect(() => {
    const video = document.getElementById('video');
    if (video != null) {
      try {
        video.load();
        video.play();
      } catch (e) {
      }
    }
  }, [slug]);

  const [categoryPageData] = data.categoryPages;
  const router = useRouter();
  const {asPath, locale} = router;
  const {
    categoryOverview,
    macroProducts,
    components: allComponents,
    SEO,
    localizations,
  } = categoryPageData;

  const {
    categoryDescription,
    categoryTitle,
    heroVideo,
    videoDescription,
    videoThumbnail,
    videoTitle,
  } = categoryOverview;

  const alternativeUrls = generateAlternativeUrlsFromLocalizations(
      localizations, router);

  const {url: videoThumbnailUrl} = videoThumbnail;
  const {url: heroVideoUrl, created_at: heroVideoCreatedAtString} = heroVideo;

  const {categoryOverview: categoryOverviewMarkdown} = markdown.categoryPage;
  const categoryUrl = `https://www.amptech.store/${locale}${asPath}`;
  const breadCrumbList = [
    {text: categoryTitle, url: categoryUrl}];
  const breadcrumbsJsonLd = generateBreadcrumbJsonLd(breadCrumbList);

  const videoJsonMeta = {
    title: videoTitle,
    description: videoDescription,
    thumbnailUrl: videoThumbnailUrl,
    contentUrl: categoryUrl,
    embedUrl: heroVideoUrl,
    uploadDateString: heroVideoCreatedAtString,
  };

  const videoJsonLd = generateVideoJsonLd(videoJsonMeta);
  const contentComponents = _.compact(allComponents.map((component, index) => {

    const {__typename: componentType} = component;
    const isFirst = index === 0;

    let parsedComponent = null;
    switch (componentType) {

      case 'ComponentGeneralPartners':
        parsedComponent =
            <Partners key={`COMPONENT_${index}`} partners={component}/>;
        break;

      case 'ComponentGeneralFullWidthProduct':
        parsedComponent = <FullWidthComponent key={`COMPONENT_${index}`}
                                              component={component}/>;
        break;

      case 'ComponentGeneralProducts':

        const {isPublic} = component;

        if (!isPublic) return null;
        parsedComponent = <ProductsComponent
            priority={false}
            key={`COMPONENT_${index}`}
            component={component}/>;
        break;

    }

    return parsedComponent;

  }));

  const isModelCategory = model.length === 1;

  const title = isModelCategory ?
      translateSiteMeta('HEADER_TITLE_CATEGORY_PAGE', {
        model: model.toUpperCase(),
        brand: translateSiteMeta('BRAND'),
        slogan: translateSiteMeta('SLOGAN'),
      }) :
      translateSiteMeta('HEADER_TITLE_CATEGORY_PAGE_JOWUA', {
        siteTitle: mainTitle.toUpperCase(), slogan: translateSiteMeta('SLOGAN'),
      });

  return (

      <>
        <MetaHead
            title={title}
            description={!!SEO && !!SEO.description ?
                SEO.description :
                categoryDescription}
            breadcrumbsJsonLd={breadcrumbsJsonLd}
            ogCustomImageUrl={!!SEO && !!SEO.thumbnail && !!SEO.thumbnail.url ?
                SEO.thumbnail.url :
                ''}
            videoJsonLd={videoJsonLd}
            alternativeUrls={alternativeUrls}
        />
        <main className={'bg-gray-50'}>
          <CategoryOverview categoryOverview={categoryOverview}
                            categoryOverviewMarkdown={categoryOverviewMarkdown}
                            isModelCategory={isModelCategory}
          />
          {!!macroProducts && macroProducts.length > 0 &&
              <MacroProducts macroProducts={macroProducts} model={model}/>}
          {contentComponents}
        </main>

      </>);
}

export async function getStaticPaths({locales, defaultLocale}) {

  const paths = [];
  for (const locale of locales) {

    const _locale = getStrapiLocale(locale);

    const {data} = await client.query({
      query: GET_CATEGORY_PAGES_COMPLETE, variables: {locale: _locale},
    });
    for (let page of data.categoryPages) {

      paths.push({
        params: {model: page.slug}, locale,
      });

    }
  }

  return {
    paths, fallback: false,
  };
}

export async function getStaticProps({params, locale, defaultLocale}) {

  const _locale = getStrapiLocale(locale);

  const {model} = params;

  const {data} = await client.query({
    query: GET_SINGLE_CATEGORY_PAGE, variables: {slug: model, locale: _locale},
  });

  const markdown = await parseMarkdown(data);

  async function parseMarkdown(data) {

    const titleMarkdown = data.categoryPages[0].categoryOverview.pageTitle;

    const processedContent = (await remark().
        use(html).
        process(titleMarkdown)).toString();

    return {
      categoryPage: {
        categoryOverview: {
          pageTitle: processedContent,
        },
      },
    };

  }

  return {
    props: {
      data, markdown, model, ...(await serverSideTranslations(locale,
          ['common', 'pages', 'products', 'site-meta', 'menu', 'order'])),
    }, revalidate: 86400,
  };
}
